import * as React from 'react'

import { type Datum } from '@nivo/legends'
import { ResponsivePie } from '@nivo/pie'
import { scaleOrdinal } from 'd3-scale'
import sumBy from 'lodash/sumBy'

import i18n from 'modules/core/lib/i18n'

import { formatterForGraph } from 'modules/reports/lib/Constants'
import type ReportStruct from 'modules/reports/lib/ReportStruct'
import { UIColors, colorList } from 'modules/reports/lib/colors'

interface Props {
  munched: any
  data: Array<Record<string, any>>
  reportStruct: ReportStruct
  showLegend: boolean
  reduceMargins: boolean
  chartData?: Record<string, any>
}

const LETTER_SIZE = 5 // Estimation
const SYMBOL_SIZE = 20

/**
 * PieChart uses the data structure from the Pie muncher to render the chart.
 *
 * The Pie muncher uses the projections (primaryField, secondaryField, tertiaryField)
 * of the Query class in order to map the data into a Pie data structure.
 *
 * The Pie muncher uses the ther the if tertiaryField exists, the bar muncher will use
 * the tertiaryField to map it into a label, on the other hand it will use the secondaryField,
 * and finally the Bar muncher maps the firstProjection into the value
 *
 * @example
 * // [
 * //   {
 * //     id: '2022-08-01',
 * //     value: 1,
 * //     label: '2022-08-01'
 * //   }
 * // ]
 *
 * const data = {
 *  count.leave.id: 1, // firstProjection
 *  cohort: '2022-08-01' // secondProjection
 * }
 * const aggregation = {
 *  function: "count",
 *  path: "leave.id"
 * }
 * const projection = {
 *  function: "count",
 *  path: "leave.id"
 * }
 *
 * const muncher = new Pie({data, aggregation, projection})
 * muncher.call()
 *
 * @param munched - provide data structure output from the Area muncher class
 * @param data - provide raw data from reports controller
 * @param reportStruct - provide reportStruct in order to read the query projections
 * @param showLegend - provide showLegend in order to show the chart legend
 * @param chartData - provide chartData in order to read the sql projections
 */
const PieChart: React.FC<Props> = ({
  munched,
  data,
  reportStruct,
  showLegend,
  reduceMargins,
  chartData,
}) => {
  const scale = React.useMemo(() => scaleOrdinal(colorList), [])

  const findLongestLabelLength = React.useCallback((data: Array<Record<string, any>>): number => {
    return Math.max(...data.map((el) => String(el.label).length))
  }, [])

  const colorForElement = React.useCallback(
    (item: Record<string, any>) => {
      const value = item.id

      if (!value) return UIColors.grey3

      return scale(value)
    },
    [scale]
  )

  /**
   * calculateMargins - returns the chart legends and its margin.
   * if showLegened is true and the reportStruct has projections available it will
   * return a hash in order to render the chart legends, however if there are not
   * projections available it return only the chart margins.
   *
   * @param legendItemSize - provide the legend size in pixels
   * @param showLegend - provide user showLegend in order to calculate the margin with chart legends
   * @param reduceMargins - provide reduced margin in order to calculate the chart canvas with less margins
   */
  const calculateMargins = React.useCallback(
    (legendItemSize: number, showLegend: boolean, reduceMargins: boolean): Record<string, any> => {
      if (!showLegend && !reduceMargins) {
        return {
          top: 30,
          right: 50,
          bottom: 60,
          left: 80,
        }
      } else if (!showLegend && reduceMargins) {
        return {
          top: 15,
          right: 25,
          bottom: 30,
          left: 40,
        }
      } else if (showLegend && !reduceMargins) {
        return {
          top: 30,
          right: legendItemSize + 50,
          bottom: 80,
          left: 50,
        }
      } else {
        return {
          top: 15,
          right: legendItemSize + 50,
          bottom: 15,
          left: 15,
        }
      }
    },
    []
  )

  /**
   * extraProps - returns the chart legends and its margin.
   * if showLegend is true and the reportStruct has projections available it will
   * return a hash in order to render the chart legends, however if there are not
   * projections available it return only the chart margins.
   *
   * @param data - provide the data structure from pie muncher
   */
  const extraProps = React.useCallback(
    (data: Array<Record<string, any>>): Partial<React.ComponentProps<typeof ResponsivePie>> => {
      const longestLabelLength: number = findLongestLabelLength(data)

      const legendItemSize: number = longestLabelLength * LETTER_SIZE + SYMBOL_SIZE

      const margin = calculateMargins(legendItemSize, showLegend, reduceMargins)

      const projection = reportStruct.props.projections[1]
      const labelFormatter = projection && formatterForGraph(projection)

      const legendLabels: Datum[] = data.map((element) => ({
        id: element.label,
        label: labelFormatter ? labelFormatter(element.label) : element.label,
        color: colorForElement(element),
      }))

      if (showLegend && (reportStruct.props.projections.length > 1 || chartData?.secondary_data)) {
        return {
          margin,
          legends: [
            {
              data: legendLabels,
              anchor: 'top-right',
              direction: 'column',
              justify: false,
              translateX: 70 + legendItemSize,
              translateY: 0,
              itemsSpacing: 5,
              itemWidth: legendItemSize,
              itemHeight: 20,
              itemDirection: 'left-to-right',
              itemOpacity: 0.85,
              symbolSize: SYMBOL_SIZE,
              padding: 40,
            },
          ],
        }
      }

      return {
        margin,
      }
    },
    [
      findLongestLabelLength,
      calculateMargins,
      showLegend,
      reduceMargins,
      reportStruct,
      chartData,
      colorForElement,
    ]
  )

  return (
    <ResponsivePie
      data={munched}
      animate
      enableArcLinkLabels={false}
      sortByValue
      tooltip={(e: any) => {
        const total = sumBy(munched, 'value')
        const percentage = ((e.datum.value / total) * 100).toFixed(1)

        return <strong>{`${e.datum.label}: ${e.datum.value} (${percentage}%)`}</strong>
      }}
      colors={colorList}
      innerRadius={0.5}
      cornerRadius={3}
      padAngle={1.5}
      valueFormat={(value: number) => i18n.formatNumber(value)}
      {...extraProps(munched)}
    />
  )
}

export default PieChart
